import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Laptop, Loader2, PackageSearch, Plus, Search, X } from 'lucide-react';
import { NO_TEAM, NO_TEAM_LABEL, type AssetSortField } from '@skhub/contracts';
import { useAuth } from '@/app/auth';
import { useToast } from '@/app/toast';
import { useAssets, useMeta } from '@/lib/queries';
import { useScrollToResults } from '@/lib/scroll';
import { CategoryDeck } from './CategoryDeck';
import { Fragment } from 'react';
import { download } from '@/lib/api';
import { num, relTime } from '@/lib/format';
import { useClampPage, CategoryIcon, EmptyState, ErrorState, LoadingRows, PageHeader, Pagination, StatusPill } from '@/components/ui';
import { DomainEyebrow } from '@/components/domain';

const FILTER_KEYS = ['q', 'type', 'status', 'team', 'location', 'assigned', 'laptop', 'quality', 'employeeId'] as const;
const QUALITY_LABEL: Record<string, string> = { 'no-serial': 'No serial number', 'no-warranty': 'No warranty date' };

export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function AssetsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const meta = useMeta();
  const [sp, setSp] = useSearchParams();
  const get = (k: string) => sp.get(k) ?? '';
  const page = Number(sp.get('page') ?? 1) || 1;
  const pageSize = Number(sp.get('pageSize') ?? 25) || 25;
  const sort = (sp.get('sort') ?? 'assetType') as AssetSortField;
  const dir = (sp.get('dir') ?? 'asc') as 'asc' | 'desc';

  const [text, setText] = useState(get('q'));
  const debounced = useDebounced(text);
  useEffect(() => {
    if (debounced !== get('q')) update({ q: debounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => setText(get('q')), [sp.get('q')]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = 'Assets · Smart Assets';
  }, []);

  const params: Record<string, string | number | undefined> = { page, pageSize, sort, dir };
  for (const k of FILTER_KEYS) if (get(k)) params[k] = get(k);
  const list = useAssets(params);
  const resultsRef = useScrollToResults(!!list.data);
  useClampPage(list.data, page, pageSize, (p) => update({ page: String(p) }, false));
  const [exporting, setExporting] = useState(false);

  function update(patch: Record<string, string | null>, resetPage = true) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.delete('page');
    setSp(next, { replace: true });
  }

  const toggleSort = (field: AssetSortField) => {
    if (sort === field) update({ dir: dir === 'asc' ? 'desc' : 'asc' }, false);
    else update({ sort: field, dir: field === 'updatedAt' || field === 'assignedDate' ? 'desc' : 'asc' }, false);
  };

  const active = FILTER_KEYS.filter((k) => get(k));
  const chips = active
    .filter((k) => k !== 'q')
    .map((k) => {
      const v = get(k);
      const label =
        k === 'laptop' ? 'Laptops only' : k === 'assigned' ? (v === 'yes' ? 'Assigned to someone' : 'Not assigned') : k === 'quality' ? QUALITY_LABEL[v] ?? v : k === 'team' && v === NO_TEAM ? `Team: ${NO_TEAM_LABEL}` : k === 'employeeId' ? 'One employee' : `${k[0]!.toUpperCase()}${k.slice(1)}: ${v}`;
      return { k, label };
    });

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { page: _p, pageSize: _s, ...filters } = params;
      await download('/reports/assets.xlsx', filters);
      toast({ kind: 'success', title: 'Export ready', detail: `${num(list.data?.total ?? 0)} assets with the current filters.` });
    } catch (e) {
      toast({ kind: 'error', title: 'Export failed', detail: (e as Error).message });
    } finally {
      setExporting(false);
    }
  };

  const laptopOnly = get('laptop') === '1';
  // Rows are grouped under a category header whenever the list is ordered by category.
  const grouped = sort === 'assetType';
  const categoryCount = (name: string) => meta.data?.categories.find((c) => c.name === name)?.assetCount ?? 0;

  return (
    <>
      <PageHeader
        eyebrow={<DomainEyebrow domain="hardware">Inventory</DomainEyebrow>}
        title={laptopOnly ? 'Laptops' : 'All assets'}
        description="The complete register of SmartKargo hardware. Share a filtered view by copying the page link."
        actions={
          <>
            {can('reports.export') && (
              <button className="btn btn-secondary" onClick={exportXlsx} disabled={exporting || !list.data?.total}>
                {exporting ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Download aria-hidden className="size-4" />} Export
              </button>
            )}
            {can('inventory.write') && (
              <Link to="/assets/new" className="btn btn-primary">
                <Plus aria-hidden className="size-4" /> New asset
              </Link>
            )}
          </>
        }
      />

      <CategoryDeck selected={get('type')} onSelect={(c) => update({ type: c })} />

      <div ref={resultsRef} className="panel overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-line p-3 sm:p-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-3" />
              <label htmlFor="asset-search" className="sr-only">
                Search assets
              </label>
              <input
                id="asset-search"
                type="search"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Search tag, serial, name, model or holder"
                className="field pl-9"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex lg:w-auto">
              <button
                aria-pressed={laptopOnly}
                onClick={() => update({ laptop: laptopOnly ? null : '1' })}
                className={`btn ${laptopOnly ? 'btn-brand' : 'btn-secondary'}`}
              >
                <Laptop aria-hidden className="size-4" /> Laptops
              </button>
              <Select label="Category" value={get('type')} onChange={(v) => update({ type: v })} options={(meta.data?.categories ?? []).map((c) => [c.name, `${c.name} (${c.assetCount})`])} />
              <Select label="Status" value={get('status')} onChange={(v) => update({ status: v })} options={(meta.data?.statuses ?? []).map((s) => [s, s])} />
              <Select
                label="Team"
                value={get('team')}
                onChange={(v) => update({ team: v })}
                options={[...(meta.data?.teams ?? []).map((t) => [t.name, t.name] as [string, string]), [NO_TEAM, NO_TEAM_LABEL]]}
              />
              <Select label="Location" value={get('location')} onChange={(v) => update({ location: v })} options={(meta.data?.locations ?? []).map((l) => [l, l])} />
              <Select
                label="Assignment"
                value={get('assigned')}
                onChange={(v) => update({ assigned: v })}
                options={[
                  ['yes', 'Assigned'],
                  ['no', 'Not assigned'],
                ]}
              />
            </div>
          </div>
          {(chips.length > 0 || get('q')) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {chips.map((c) => (
                <button key={c.k} onClick={() => update({ [c.k]: null })} className="inline-flex h-7 items-center gap-1 rounded-full border border-line bg-surface-2 pr-2 pl-2.5 text-xs text-fg-2 hover:border-line-strong hover:text-fg" aria-label={`Remove filter ${c.label}`}>
                  {c.label} <X aria-hidden className="size-3" />
                </button>
              ))}
              <button onClick={() => update(Object.fromEntries(FILTER_KEYS.map((k) => [k, null])))} className="ml-1 text-xs font-medium text-brand-ink hover:underline">
                Clear all
              </button>
            </div>
          )}
        </div>

        <div aria-live="polite" className="sr-only">
          {list.data ? `${list.data.total} assets match` : ''}
        </div>

        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : !list.data ? (
          <LoadingRows rows={10} cols={5} />
        ) : list.data.items.length === 0 ? (
          <EmptyState
            icon={PackageSearch}
            title={active.length ? 'No assets match these filters' : 'No assets in the register yet'}
            action={
              active.length ? (
                <button className="btn btn-secondary btn-sm" onClick={() => update(Object.fromEntries(FILTER_KEYS.map((k) => [k, null])))}>
                  Clear filters
                </button>
              ) : can('inventory.write') ? (
                <Link to="/assets/new" className="btn btn-primary btn-sm">
                  Add the first asset
                </Link>
              ) : undefined
            }
          >
            {active.length ? 'Try a broader search or remove a filter.' : undefined}
          </EmptyState>
        ) : (
          <div className={list.isFetching ? 'opacity-70 transition-opacity' : 'transition-opacity'}>
            {/* Desktop / tablet table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-left text-[13px]">
                <caption className="sr-only">Assets, sorted by {sort} {dir === 'asc' ? 'ascending' : 'descending'}</caption>
                <thead className="border-b border-line bg-surface-2/60 text-xs text-fg-3">
                  <tr>
                    <SortTh field="assetTag" label="Asset" sort={sort} dir={dir} onSort={toggleSort} className="pl-5" />
                    <SortTh field="assetType" label="Category" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortTh field="status" label="Status" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortTh field="assignee" label="Holder" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortTh field="updatedAt" label="Updated" sort={sort} dir={dir} onSort={toggleSort} className="pr-5 text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {list.data.items.map((a, i, rows) => (
                    <Fragment key={a.id}>
                    {grouped && (i === 0 || rows[i - 1]!.assetType !== a.assetType) && (
                      <tr data-group className="bg-surface-2/50">
                        <th scope="rowgroup" colSpan={5} className="py-2 pr-5 pl-5 text-left">
                          <span className="flex items-center gap-2.5 text-[12px] font-semibold text-fg">
                            <span className={`grid size-6 place-items-center rounded-[6px] ${a.isLaptop ? 'bg-brand-soft text-brand-ink' : 'bg-surface-3 text-fg-2'}`}>
                              <CategoryIcon type={a.assetType} className="size-3.5" />
                            </span>
                            {a.assetType}
                            <span className="font-normal text-fg-3 tnum">{num(categoryCount(a.assetType))} in the register</span>
                          </span>
                        </th>
                      </tr>
                    )}
                    <tr onClick={() => navigate(`/assets/${a.id}`)} className="cursor-pointer transition-colors hover:bg-surface-2">
                      <td className="py-2.5 pr-3 pl-5">
                        <Link to={`/assets/${a.id}`} onClick={(e) => e.stopPropagation()} className="mono text-[13px] font-medium text-fg hover:text-brand-ink">
                          {a.assetTag}
                        </Link>
                        <p className="max-w-[320px] truncate text-xs text-fg-3">
                          {a.name}
                          {a.serialNumber ? <span className="mono"> · {a.serialNumber}</span> : null}
                        </p>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-2 ${a.isLaptop ? 'text-fg' : 'text-fg-2'}`}>
                          <CategoryIcon type={a.assetType} className={`size-4 ${a.isLaptop ? 'text-brand' : 'text-fg-3'}`} />
                          {a.assetType}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <StatusPill status={a.status} />
                      </td>
                      <td className="px-3 py-2.5">
                        {a.assignee ? (
                          <>
                            <p className="max-w-[220px] truncate text-fg">{a.assignee.displayName}</p>
                            <p className="text-xs text-fg-3">
                              <span className="mono">{a.assignee.employeeCode}</span>
                              {a.assignee.team ? ` · ${a.assignee.team}` : ''}
                            </p>
                          </>
                        ) : (
                          <span className="text-fg-3">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-5 pl-3 text-right text-xs text-fg-3">
                        <time dateTime={a.updatedAt}>{relTime(a.updatedAt)}</time>
                      </td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phone: cards */}
            <ul className="divide-y divide-line md:hidden">
              {list.data.items.map((a) => (
                <li key={a.id}>
                  <Link to={`/assets/${a.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-surface-2">
                    <span className={`mt-0.5 grid size-9 shrink-0 place-items-center rounded-[var(--radius-md)] ${a.isLaptop ? 'bg-brand-soft text-brand-ink' : 'bg-surface-2 text-fg-3'}`}>
                      <CategoryIcon type={a.assetType} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="mono text-[13px] font-medium text-fg">{a.assetTag}</span>
                        <StatusPill status={a.status} />
                      </span>
                      <span className="block truncate text-xs text-fg-2">{a.name}</span>
                      {a.assignee && <span className="block truncate text-xs text-fg-3">{a.assignee.displayName}</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex-1">
                <Pagination page={page} pageSize={pageSize} total={list.data.total} onPage={(p) => update({ page: String(p) }, false)} />
              </div>
              <label className="flex items-center gap-2 border-t border-line px-4 py-2 text-xs text-fg-3 sm:border-l">
                Rows
                <select className="field h-8 w-[4.5rem] text-xs" value={pageSize} onChange={(e) => update({ pageSize: e.target.value })}>
                  {[25, 50, 100].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <label className="block min-w-0 lg:w-36">
      <span className="sr-only">{label}</span>
      <select className={`field ${value ? 'border-fg-3 font-medium text-fg' : 'text-fg-2'}`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{label}: all</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortTh({ field, label, sort, dir, onSort, className = '' }: { field: AssetSortField; label: string; sort: AssetSortField; dir: 'asc' | 'desc'; onSort: (f: AssetSortField) => void; className?: string }) {
  const activeSort = sort === field;
  const Icon = !activeSort ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th scope="col" aria-sort={activeSort ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} className={`px-3 py-2.5 font-medium ${className}`}>
      <button onClick={() => onSort(field)} className={`inline-flex items-center gap-1 hover:text-fg ${activeSort ? 'text-fg' : ''}`}>
        {label}
        <Icon aria-hidden className={`size-3 ${activeSort ? '' : 'opacity-40'}`} />
      </button>
    </th>
  );
}

